/**
 * Type Adapters
 * TASK-294: Convert between search types and data gathering types
 *
 * Maps SearchResponse/SourceAttribution to DataGathering types
 */

import type { SourceAttribution, SourceType } from '../../types/search.types';
import type {
  DataSourceType,
  SourceCitation,
  SourceItem,
  Insight,
  DataSource,
} from '../../types/dataGathering';
import type { MeetingPrepSource, MeetingPrepSourceType } from './types';
import { formatDate as formatDateCore } from '../formatters';

/**
 * Map search SourceType to DataGathering DataSourceType
 */
export function mapSourceType(sourceType: SourceType): DataSourceType {
  const mapping: Record<SourceType, DataSourceType> = {
    email: 'email',
    database: 'notes',
    market: 'market_news',
    news: 'news',
    document: 'notes',
    calendar: 'calendar',
    portfolio_alert: 'market_news',
    kg_fact: 'notes', // TASK-605: KG facts fold into the notes bucket for prep
  };
  return mapping[sourceType] || 'notes';
}

/**
 * Map MeetingPrepSourceType to DataGathering DataSourceType
 */
export function mapMeetingPrepSourceType(sourceType: MeetingPrepSourceType): DataSourceType {
  const mapping: Record<MeetingPrepSourceType, DataSourceType> = {
    client: 'notes',
    email: 'email',
    market: 'market_news',
    news: 'news',
    documents: 'notes',
    calendar: 'calendar',
    portfolio: 'market_news',
  };
  return mapping[sourceType] || 'notes';
}

/**
 * Map DataGathering DataSourceType to MeetingPrepSourceType
 */
export function mapDataSourceType(sourceType: DataSourceType): MeetingPrepSourceType {
  const mapping: Partial<Record<DataSourceType, MeetingPrepSourceType>> = {
    notes: 'documents',
    email: 'email',
    market_news: 'market',
    news: 'news',
    meeting_notes: 'documents',
    life_events: 'client',
    calendar: 'documents',
  };
  return mapping[sourceType] || 'documents';
}

/**
 * Convert SourceAttribution to SourceCitation
 */
export function toSourceCitation(source: SourceAttribution): SourceCitation {
  return {
    type: mapSourceType(source.sourceType),
    id: source.sourceId,
    title: source.title || 'Untitled',
    date: source.timestamp || new Date().toISOString(),
    excerpt: source.excerpt,
    relevance: source.relevanceScore,
  };
}

/**
 * Convert SourceAttribution to SourceItem
 */
export function toSourceItem(source: SourceAttribution): SourceItem {
  const subtitle = buildSubtitle(source);

  return {
    id: source.sourceId,
    title: source.title || 'Untitled',
    subtitle,
    preview: source.excerpt,
    metadata: source.metadata || {},
    date: source.timestamp,
    sentiment: inferSentiment(source),
  };
}

/**
 * Build subtitle based on source type
 */
function buildSubtitle(source: SourceAttribution): string {
  const metadata = source.metadata || {};

  switch (source.sourceType) {
    case 'email':
      return metadata.from ? `From: ${metadata.from}` : `Date: ${formatDate(source.timestamp)}`;
    case 'database':
      return metadata.category ? `Category: ${metadata.category}` : 'Client Data';
    case 'market':
      return metadata.symbol ? `Symbol: ${metadata.symbol}` : 'Market Data';
    case 'news':
      return metadata.source ? `Source: ${metadata.source}` : `Date: ${formatDate(source.timestamp)}`;
    case 'document':
      return metadata.type ? `Type: ${metadata.type}` : `Date: ${formatDate(source.timestamp)}`;
    default:
      return `Date: ${formatDate(source.timestamp)}`;
  }
}

/**
 * Format date for display, with null-safety for optional timestamps
 */
function formatDate(isoDate?: string): string {
  if (!isoDate) return 'Unknown date';
  try {
    return formatDateCore(isoDate);
  } catch {
    return 'Unknown date';
  }
}

/**
 * Infer sentiment from source content
 */
function inferSentiment(source: SourceAttribution): 'positive' | 'neutral' | 'negative' | undefined {
  const text = `${source.title || ''} ${source.excerpt}`.toLowerCase();

  const positiveWords = ['growth', 'increase', 'gain', 'profit', 'success', 'excellent', 'great'];
  const negativeWords = ['loss', 'decline', 'risk', 'concern', 'issue', 'problem', 'warning'];

  const hasPositive = positiveWords.some((w) => text.includes(w));
  const hasNegative = negativeWords.some((w) => text.includes(w));

  if (hasPositive && !hasNegative) return 'positive';
  if (hasNegative && !hasPositive) return 'negative';
  return 'neutral';
}

/**
 * Convert MeetingPrepSource to DataSource
 */
export function toDataSource(source: MeetingPrepSource): DataSource {
  const dataSourceType = mapMeetingPrepSourceType(source.type);
  const items = source.data?.sources.map(toSourceItem) || [];

  return {
    type: dataSourceType,
    name: getSourceName(source.type),
    description: getSourceDescription(source.type),
    icon: getSourceIcon(source.type),
    status: mapStatus(source.status),
    recordCount: source.itemCount,
    items,
    hasMore: items.length >= 20,
    error: source.error,
    lastUpdated: new Date().toISOString(),
  };
}

/**
 * Get display name for source type
 */
function getSourceName(type: MeetingPrepSourceType): string {
  const names: Record<MeetingPrepSourceType, string> = {
    client: 'Client Profile',
    email: 'Email & Communications',
    market: 'Market Data',
    news: 'News & Insights',
    documents: 'Documents',
    calendar: 'Calendar Events',
    portfolio: 'Portfolio Alerts',
  };
  return names[type] || type;
}

/**
 * Get description for source type
 */
function getSourceDescription(type: MeetingPrepSourceType): string {
  const descriptions: Record<MeetingPrepSourceType, string> = {
    client: 'Client profile and risk assessment',
    email: 'Recent email communications',
    market: 'Market data for portfolio holdings',
    news: 'News about client and holdings',
    documents: 'Related documents and reports',
    calendar: 'Upcoming meetings and appointments',
    portfolio: 'Portfolio alerts and breaches',
  };
  return descriptions[type] || '';
}

/**
 * Get icon for source type
 */
function getSourceIcon(type: MeetingPrepSourceType): string {
  const icons: Record<MeetingPrepSourceType, string> = {
    client: '👤',
    email: '📧',
    market: '📈',
    news: '📰',
    documents: '📄',
    calendar: '📅',
    portfolio: '⚠️',
  };
  return icons[type] || '📋';
}

/**
 * Map MeetingPrepSourceStatus to DataSourceStatus
 */
function mapStatus(status: MeetingPrepSource['status']): DataSource['status'] {
  const mapping: Record<string, DataSource['status']> = {
    pending: 'pending',
    loading: 'gathering',
    completed: 'completed',
    error: 'error',
    partial: 'completed', // Show as completed with warning
  };
  return mapping[status] || 'pending';
}

/**
 * Convert search response to insight
 */
export function toInsight(
  source: SourceAttribution,
  index: number,
  category: 'positive' | 'neutral' | 'alert' | 'action_needed' = 'neutral'
): Insight {
  return {
    id: `insight-${index}-${source.sourceId}`,
    title: source.title || 'Key Finding',
    description: source.excerpt,
    category,
    confidence: source.relevanceScore,
    timestamp: source.timestamp,
    sources: [toSourceCitation(source)],
  };
}

/**
 * Batch convert sources to insights
 */
export function toInsights(
  sources: SourceAttribution[],
  category: 'positive' | 'neutral' | 'alert' | 'action_needed' = 'neutral'
): Insight[] {
  return sources.map((source, index) => toInsight(source, index, category));
}
